<template>
  <div class="tier-work-group">
    <div class="tier-work-group__content">
      <!-- Левая колонка - Группы работ -->
      <div class="tier-work-group__column">
        <div class="column-header">
          <q-icon name="groups" size="14px" />
          <span class="column-title">Группы работ</span>
          <q-badge v-if="workGroups.length" :label="filteredWorkGroups.length + '/' + workGroups.length" color="grey-4" text-color="grey-8" />
          <q-btn
            v-if="selectedWorkGroups.length > 0"
            icon="delete"
            flat
            dense
            round
            size="xs"
            color="negative"
            @click="confirmDeleteSelectedWorkGroups"
          >
            <q-tooltip>Удалить выбранные ({{ selectedWorkGroups.length }})</q-tooltip>
          </q-btn>
          <q-btn
            icon="add"
            flat
            dense
            round
            size="xs"
            color="primary"
            @click="openWorkGroupDialog()"
          >
            <q-tooltip>Добавить</q-tooltip>
          </q-btn>
        </div>
        <div v-if="workGroups.length" class="search-container">
          <q-input
            v-model="workGroupSearchQuery"
            dense
            outlined
            placeholder="Поиск..."
            clearable
            class="search-input"
          >
            <template v-slot:prepend>
              <q-icon name="search" size="16px" />
            </template>
          </q-input>
        </div>
        <q-table
          v-if="!isLoadingWorkGroups && filteredWorkGroups.length > 0"
          :rows="filteredWorkGroups"
          :columns="workGroupColumns"
          row-key="work_group_dict_id"
          :selected="selectedWorkGroupsRows"
          selection="multiple"
          @selection="onWorkGroupSelection"
          :rows-per-page-options="[0]"
          hide-pagination
          flat
          dense
          class="column-table"
          :class="{ 'table-selected': selectedWorkGroup }"
        >
          <template v-slot:body="props">
            <q-tr
              :props="props"
              :class="{ 'row-selected': selectedWorkGroup?.work_group_dict_id === props.row.work_group_dict_id }"
              @click="selectWorkGroup(props.row)"
              class="table-row-clickable"
            >
              <q-td auto-width>
                <q-checkbox
                  :model-value="selectedWorkGroups.includes(props.row.work_group_dict_id)"
                  @update:model-value="(val) => toggleWorkGroupSelection(props.row.work_group_dict_id, val)"
                  @click.stop
                  size="xs"
                  dense
                />
              </q-td>
              <q-td>{{ props.row.work_group_name }}</q-td>
              <q-td>{{ props.row.work_group_dict_id }}</q-td>
              <q-td>
                <q-icon v-if="props.row.is_level_need" name="check" color="positive" size="16px" />
                <span v-else class="text-grey-5">-</span>
              </q-td>
              <q-td auto-width>
                <q-btn
                  icon="edit"
                  flat
                  dense
                  round
                  size="xs"
                  color="primary"
                  @click.stop="openWorkGroupDialog(props.row)"
                >
                  <q-tooltip>Редактировать</q-tooltip>
                </q-btn>
              </q-td>
            </q-tr>
          </template>
        </q-table>
        <div v-else-if="!isLoadingWorkGroups && filteredWorkGroups.length === 0" class="empty-state">
          <q-icon name="search_off" color="grey-5" />
          <p>{{ workGroupSearchQuery ? 'Ничего не найдено' : 'Нет групп работ' }}</p>
        </div>
        <div v-else class="loading-container">
          <q-spinner color="primary" size="30px" />
        </div>
      </div>

      <!-- Правая колонка - Задачи -->
      <div class="tier-work-group__column">
        <div class="column-header">
          <q-icon name="task" size="14px" />
          <span class="column-title">Задачи</span>
          <q-badge v-if="tasks.length" :label="filteredTasks.length + '/' + tasks.length" color="grey-4" text-color="grey-8" />
          <q-btn
            v-if="selectedTasks.length > 0"
            icon="delete"
            flat
            dense
            round
            size="xs"
            color="negative"
            @click="confirmDeleteSelectedTasks"
          >
            <q-tooltip>Удалить выбранные ({{ selectedTasks.length }})</q-tooltip>
          </q-btn>
          <q-btn
            v-if="selectedWorkGroup"
            icon="add"
            flat
            dense
            round
            size="xs"
            color="primary"
            @click="openTaskDialog()"
          >
            <q-tooltip>Добавить</q-tooltip>
          </q-btn>
        </div>
        <div v-if="selectedWorkGroup && tasks.length" class="search-container">
          <q-input
            v-model="taskSearchQuery"
            dense
            outlined
            placeholder="Поиск..."
            clearable
            class="search-input"
          >
            <template v-slot:prepend>
              <q-icon name="search" size="16px" />
            </template>
          </q-input>
        </div>
        <div v-if="!selectedWorkGroup" class="empty-state">
          <q-icon name="arrow_back" color="grey-5" />
          <p>← Выберите группу работ</p>
        </div>
        <q-table
          v-else-if="!isLoadingTasks && filteredTasks.length > 0"
          :rows="filteredTasks"
          :columns="taskColumns"
          row-key="task_dict_id"
          :selected="selectedTasksRows"
          selection="multiple"
          @selection="onTaskSelection"
          :rows-per-page-options="[0]"
          hide-pagination
          flat
          dense
          class="column-table"
        >
          <template v-slot:body="props">
            <q-tr :props="props">
              <q-td auto-width>
                <q-checkbox
                  :model-value="selectedTasks.includes(props.row.task_dict_id)"
                  @update:model-value="(val) => toggleTaskSelection(props.row.task_dict_id, val)"
                  @click.stop
                  size="xs"
                  dense
                />
              </q-td>
              <q-td>{{ props.row.task_name }}</q-td>
              <q-td>{{ props.row.task_dict_id }}</q-td>
              <q-td>{{ props.row.unit_name || '-' }}</q-td>
              <q-td>{{ props.row.order_num !== null ? props.row.order_num : '-' }}</q-td>
              <q-td auto-width>
                <q-btn
                  icon="edit"
                  flat
                  dense
                  round
                  size="xs"
                  color="primary"
                  @click.stop="openTaskDialog(props.row)"
                >
                  <q-tooltip>Редактировать</q-tooltip>
                </q-btn>
              </q-td>
            </q-tr>
          </template>
        </q-table>
        <div v-else-if="!isLoadingTasks && filteredTasks.length === 0" class="empty-state">
          <q-icon name="search_off" color="grey-5" />
          <p>{{ taskSearchQuery ? 'Ничего не найдено' : 'Нет задач' }}</p>
        </div>
        <div v-else-if="isLoadingTasks" class="loading-container">
          <q-spinner color="primary" size="30px" />
        </div>
      </div>
    </div>

    <!-- Диалоги подтверждения -->
    <q-dialog v-model="confirmDialog.show" persistent>
      <q-card style="min-width: 350px">
        <q-card-section>
          <div class="text-h6">{{ confirmDialog.title }}</div>
        </q-card-section>

        <q-card-section>
          {{ confirmDialog.message }}
        </q-card-section>

        <q-card-actions align="right">
          <q-btn flat label="Отмена" color="grey" @click="confirmDialog.show = false" />
          <q-btn unelevated label="Удалить" color="negative" @click="onConfirmDelete" />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script>
import { useCustomSnackbar } from "@/_helpers/hooks/useCustomSnackbar";
import { useAppStore } from '@/services/store/app';
import {
  getWorkGroups,
  saveWorkGroup,
  deleteWorkGroup,
  getTasks,
  saveTask,
  deleteTask
} from "@/views/TierWorkGroup/services";
import { MODAL_NAMES_AIDAR } from "@/constants/index_aidar";

export default {
  name: "TierWorkGroup",
  data() {
    return {
      workGroups: [],
      tasks: [],
      selectedWorkGroup: null,
      isLoadingWorkGroups: false,
      isLoadingTasks: false,
      workGroupSearchQuery: '',
      taskSearchQuery: '',
      selectedWorkGroups: [],
      selectedTasks: [],
      workGroupColumns: [
        { name: 'work_group_name', label: 'Название', field: 'work_group_name', align: 'left', sortable: true },
        { name: 'work_group_dict_id', label: 'ID', field: 'work_group_dict_id', align: 'left', sortable: true },
        { name: 'is_level_need', label: 'Требуется этаж', field: 'is_level_need', align: 'center', sortable: true },
        { name: 'actions', label: '', field: 'actions', align: 'right', sortable: false }
      ],
      taskColumns: [
        { name: 'task_name', label: 'Название', field: 'task_name', align: 'left', sortable: true },
        { name: 'task_dict_id', label: 'ID', field: 'task_dict_id', align: 'left', sortable: true },
        { name: 'unit_name', label: 'Ед. изм.', field: 'unit_name', align: 'left', sortable: true },
        { name: 'order_num', label: 'Порядок', field: 'order_num', align: 'left', sortable: true },
        { name: 'actions', label: '', field: 'actions', align: 'right', sortable: false }
      ],
      confirmDialog: {
        show: false,
        title: '',
        message: '',
        onConfirm: null
      },
    };
  },
  computed: {
    filteredWorkGroups() {
      if (!this.workGroupSearchQuery) return this.workGroups;
      const query = this.workGroupSearchQuery.toLowerCase();
      return this.workGroups.filter(wg => 
        wg.work_group_name.toLowerCase().includes(query) ||
        wg.work_group_dict_id.toString().includes(query)
      );
    },
    filteredTasks() {
      let filtered = this.tasks.filter(task => 
        task.task_dict_code !== 'MEN' && 
        task.task_name !== 'Выход людей'
      );
      
      if (!this.taskSearchQuery) return filtered;
      const query = this.taskSearchQuery.toLowerCase();
      return filtered.filter(task => 
        task.task_name.toLowerCase().includes(query) ||
        task.task_dict_id.toString().includes(query)
      );
    },
    selectedWorkGroupsRows() {
      return this.workGroups.filter(wg => this.selectedWorkGroups.includes(wg.work_group_dict_id));
    },
    selectedTasksRows() {
      return this.tasks.filter(task => this.selectedTasks.includes(task.task_dict_id));
    }
  },
  methods: {
    async loadWorkGroups() {
      this.isLoadingWorkGroups = true;
      const res = await getWorkGroups();
      this.isLoadingWorkGroups = false;
      if (!res) return;
      this.workGroups = res;
    },
    async loadWorkGroupsSilently() {
      const res = await getWorkGroups();
      if (!res) return;
      this.workGroups = res;
    },
    async selectWorkGroup(workGroup) {
      this.selectedWorkGroup = workGroup;
      this.selectedTasks = [];
      this.taskSearchQuery = '';
      
      this.isLoadingTasks = true;
      const res = await getTasks(workGroup.work_group_dict_id);
      this.isLoadingTasks = false;
      if (!res) return;
      this.tasks = res;
    },
    openWorkGroupDialog(workGroup = null) {
      this.showModal(MODAL_NAMES_AIDAR[workGroup ? 'EditWorkGroupDict' : 'AddWorkGroupDict'], {
        title: workGroup ? 'Редактировать группу работ' : 'Добавить группу работ',
        workGroup: workGroup,
        onSubmit: async (data) => {
          const res = await saveWorkGroup(data);
          if (res) {
            this.showSucceedSnackbar(workGroup ? 'Группа работ обновлена' : 'Группа работ добавлена');
            await this.loadWorkGroupsSilently();
            
            // Если редактировали выбранную группу, обновляем её
            if (workGroup && this.selectedWorkGroup?.work_group_dict_id === workGroup.work_group_dict_id) {
              const updated = this.workGroups.find(wg => wg.work_group_dict_id === workGroup.work_group_dict_id);
              if (updated) {
                await this.selectWorkGroup(updated);
              }
            }
          }
        }
      });
    },
    openTaskDialog(task = null) {
      if (!this.selectedWorkGroup) return;
      
      this.showModal(MODAL_NAMES_AIDAR[task ? 'EditTaskDict' : 'AddTaskDict'], {
        title: task ? 'Редактировать задачу' : 'Добавить задачу',
        task: task,
        workGroupDictId: this.selectedWorkGroup.work_group_dict_id,
        onSubmit: async (data) => {
          const res = await saveTask(data);
          if (res) {
            this.showSucceedSnackbar(task ? 'Задача обновлена' : 'Задача добавлена');
            await this.loadTasksSilently(this.selectedWorkGroup.work_group_dict_id);
          }
        }
      });
    },
    async loadTasksSilently(work_group_dict_id) {
      const res = await getTasks(work_group_dict_id);
      if (!res) return;
      this.tasks = res;
    },
    toggleWorkGroupSelection(workGroupId, selected) {
      if (selected) {
        if (!this.selectedWorkGroups.includes(workGroupId)) {
          this.selectedWorkGroups.push(workGroupId);
        }
      } else {
        const index = this.selectedWorkGroups.indexOf(workGroupId);
        if (index > -1) {
          this.selectedWorkGroups.splice(index, 1);
        }
      }
    },
    onWorkGroupSelection({ rows, added, removed }) {
      // Обработка множественного выбора через таблицу
      if (added && added.length > 0) {
        added.forEach(row => {
          if (!this.selectedWorkGroups.includes(row.work_group_dict_id)) {
            this.selectedWorkGroups.push(row.work_group_dict_id);
          }
        });
      }
      if (removed && removed.length > 0) {
        removed.forEach(row => {
          const index = this.selectedWorkGroups.indexOf(row.work_group_dict_id);
          if (index > -1) {
            this.selectedWorkGroups.splice(index, 1);
          }
        });
      }
    },
    toggleTaskSelection(taskId, selected) {
      if (selected) {
        if (!this.selectedTasks.includes(taskId)) {
          this.selectedTasks.push(taskId);
        }
      } else {
        const index = this.selectedTasks.indexOf(taskId);
        if (index > -1) {
          this.selectedTasks.splice(index, 1);
        }
      }
    },
    onTaskSelection({ rows, added, removed }) {
      // Обработка множественного выбора через таблицу
      if (added && added.length > 0) {
        added.forEach(row => {
          if (!this.selectedTasks.includes(row.task_dict_id)) {
            this.selectedTasks.push(row.task_dict_id);
          }
        });
      }
      if (removed && removed.length > 0) {
        removed.forEach(row => {
          const index = this.selectedTasks.indexOf(row.task_dict_id);
          if (index > -1) {
            this.selectedTasks.splice(index, 1);
          }
        });
      }
    },
    confirmDeleteSelectedWorkGroups() {
      this.confirmDialog = {
        show: true,
        title: 'Подтверждение',
        message: `Вы уверены, что хотите удалить ${this.selectedWorkGroups.length} групп работ?`,
        onConfirm: async () => {
          await this.deleteSelectedWorkGroups();
        }
      };
    },
    async deleteSelectedWorkGroups() {
      const workGroupsToDelete = [...this.selectedWorkGroups];
      
      for (const workGroupId of workGroupsToDelete) {
        await deleteWorkGroup(workGroupId);
      }

      this.confirmDialog.show = false;
      this.selectedWorkGroups = [];
      this.showSucceedSnackbar(`Удалено ${workGroupsToDelete.length} групп работ`);
      
      // Если удаляем выбранную группу, сбрасываем выбор
      if (this.selectedWorkGroup && workGroupsToDelete.includes(this.selectedWorkGroup.work_group_dict_id)) {
        this.selectedWorkGroup = null;
        this.tasks = [];
      }
      
      await this.loadWorkGroupsSilently();
    },
    confirmDeleteSelectedTasks() {
      this.confirmDialog = {
        show: true,
        title: 'Подтверждение',
        message: `Вы уверены, что хотите удалить ${this.selectedTasks.length} задач?`,
        onConfirm: async () => {
          await this.deleteSelectedTasks();
        }
      };
    },
    async deleteSelectedTasks() {
      const tasksToDelete = [...this.selectedTasks];
      
      for (const taskId of tasksToDelete) {
        await deleteTask(taskId);
      }

      this.confirmDialog.show = false;
      this.selectedTasks = [];
      this.showSucceedSnackbar(`Удалено ${tasksToDelete.length} задач`);
      
      await this.loadTasksSilently(this.selectedWorkGroup.work_group_dict_id);
    },
    async onConfirmDelete() {
      this.confirmDialog.show = false;
      if (this.confirmDialog.onConfirm) {
        await this.confirmDialog.onConfirm();
      }
    }
  },
  mounted() {
    this.loadWorkGroups();
  },
  setup() {
    const { showSucceedSnackbar } = useCustomSnackbar();
    const { showModal } = useAppStore();
    return { showSucceedSnackbar, showModal };
  },
};
</script>

<style scoped>
.tier-work-group {
  padding: 8px;
  height: calc(100vh - 80px);
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.tier-work-group__content {
  display: flex;
  gap: 6px;
  flex: 1;
  overflow: hidden;
  min-height: 0;
}

.tier-work-group__column {
  display: flex;
  flex-direction: column;
  background: white;
  border: 1px solid #ddd;
  overflow: hidden;
  flex: 1;
  min-width: 0;
}

.column-header {
  background: #f0f0f0;
  padding: 4px 8px;
  color: #333;
  font-weight: 600;
  font-size: 11px;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  border-bottom: 1px solid #ddd;
}

.column-title {
  flex: 1;
}

.search-container {
  padding: 6px 8px;
  background: #ffffff;
  border-bottom: 1px solid #e0e0e0;
}

.search-input {
  font-size: 12px;
}

.search-input :deep(.q-field__control) {
  height: 28px;
  min-height: 28px;
}

.search-input :deep(.q-field__marginal) {
  height: 28px;
}

.search-input :deep(input) {
  font-size: 12px;
  padding: 2px 4px;
}

.column-table {
  flex: 1;
  overflow: auto;
  min-height: 0;
}

.column-table :deep(.q-table__container) {
  height: 100%;
}

.column-table :deep(.q-table__top) {
  display: none;
}

.column-table :deep(.q-table thead tr th) {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #f5f5f5;
  font-weight: 600;
  color: #333;
  font-size: 11px;
  padding: 6px 8px;
  border-bottom: 1px solid #ddd;
}

.column-table :deep(.q-table tbody tr) {
  font-size: 11px;
  cursor: pointer;
}

.column-table :deep(.q-table tbody td) {
  padding: 6px 8px;
  border-bottom: 1px solid #f0f0f0;
}

.column-table :deep(.q-table tbody tr:hover) {
  background: #e8f4fd;
}

.table-row-clickable {
  cursor: pointer;
}

.row-selected {
  background: #cce5ff !important;
  border-left: 2px solid #1976d2;
}

.column-table :deep(.q-table__card) {
  box-shadow: none;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #999;
  gap: 6px;
  padding: 16px;
}

.empty-state p {
  margin: 0;
  font-size: 11px;
}

.empty-state .q-icon {
  font-size: 28px !important;
}

.loading-container {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 80px;
}

.column-header .q-btn {
  padding: 2px;
  min-width: 24px;
  min-height: 24px;
}

.column-header .q-icon {
  font-size: 14px;
}

.column-header .q-badge {
  font-size: 10px;
  padding: 2px 4px;
}
</style>

